/**
 * Prompts resource - /api/prompts endpoints
 */
import { getLogger } from '@qwickapps/logging';
const log = getLogger('QwickForge.MiddlewareClient.PromptsResource');
export class PromptsResource {
    http;
    writeBuffer;
    constructor(http, writeBuffer) {
        this.http = http;
        this.writeBuffer = writeBuffer;
    }
    /**
     * Create a new prompt
     */
    async create(sessionId, promptText, agentName, metadata) {
        const promptId = crypto.randomUUID();
        const now = new Date();
        log.debug('Creating prompt', { promptId, sessionId, agentName });
        let serverResponse;
        if (this.writeBuffer) {
            // Add to write buffer
            this.writeBuffer.add({
                type: 'prompt',
                sessionId,
                data: {
                    id: promptId,
                    sessionId,
                    agentName,
                    promptText,
                    metadata,
                    createdAt: now.toISOString(),
                },
                timestamp: Date.now(),
            });
            // Immediately flush for CLI usage
            await this.writeBuffer.flush();
        }
        else {
            // Direct write
            const requestBody = {
                promptText,
            };
            if (metadata)
                requestBody.metadata = metadata;
            serverResponse = await this.http.post(`/api/sessions/${sessionId}/prompts`, requestBody);
        }
        const actualPromptId = serverResponse?.prompt?.id || promptId;
        log.info('Prompt created', { promptId: actualPromptId, sessionId });
        // Return minimal prompt object with fields from server response
        return {
            id: actualPromptId,
            sessionId: serverResponse?.prompt?.sessionId || sessionId,
            deviceId: '', // Not in response, will be populated by server
            orgId: '', // Not in response, will be populated by server
            userId: null,
            projectId: null,
            promptText: serverResponse?.prompt?.promptText || promptText,
            metadata: metadata || null,
            model: serverResponse?.prompt?.model || null,
            provider: serverResponse?.prompt?.provider || null,
            agentName,
            currentDir: process.cwd(), // Required field - current working directory
            responseText: null,
            inputTokens: null,
            outputTokens: null,
            totalTokens: null,
            promptHash: null,
            latencyMs: null,
            costUsd: null,
            createdAt: serverResponse?.prompt?.createdAt ? new Date(serverResponse.prompt.createdAt) : now,
        };
    }
    /**
     * Send prompt from web interface (different endpoint than create)
     */
    async sendFromWeb(params) {
        log.debug('Sending prompt from web', { deviceId: params.device_id, model: params.model });
        const response = await this.http.post('/api/prompts', params);
        log.info('Prompt sent from web', { promptId: response.id, sessionId: response.sessionId });
        return response;
    }
    /**
     * Get prompt by ID
     */
    async get(promptId) {
        try {
            log.debug('Getting prompt', { promptId });
            const prompt = await this.http.get(`/api/prompts/${promptId}`);
            return prompt;
        }
        catch (error) {
            if (error.statusCode === 404) {
                log.debug('Prompt not found', { promptId });
                return undefined;
            }
            throw error;
        }
    }
    /**
     * List prompts for a session
     */
    async list(sessionId, limit = 50, offset = 0) {
        const params = new URLSearchParams();
        params.set('limit', limit.toString());
        params.set('offset', offset.toString());
        log.debug('Listing prompts', { sessionId, limit, offset });
        const response = await this.http.get(`/api/sessions/${sessionId}/prompts?${params.toString()}`);
        log.debug('Prompts listed', { count: response.data.length, total: response.total });
        return response;
    }
    /**
     * Record prompt response
     */
    async recordResponse(record) {
        log.debug('Recording prompt response', { promptId: record.promptId });
        await this.http.patch(`/api/prompts/${record.promptId}`, {
            model: record.model,
            provider: record.provider,
            agentName: record.agentName,
            responseText: record.responseText,
            inputTokens: record.inputTokens,
            outputTokens: record.outputTokens,
            latencyMs: record.latencyMs,
            costUsd: record.costUsd,
            metadata: record.metadata,
        });
        log.debug('Prompt response recorded', { promptId: record.promptId });
    }
}
//# sourceMappingURL=PromptsResource.js.map